import './BimViewerComponent.css';
import type { ForwardedRef } from 'react';
import { Grid, CameraType, Viewpoint, Viewer, Product, State, ViewType, RenderingMode, ProductType, NavigationCube, LoaderOverlay } from '@xbim/viewer'
import { useEffect, useRef, forwardRef } from 'react';
import { BimViewerProps, TypedBimViewerComponent } from './types'
import { reforwardRef } from './utils'
import { useNavigate, useLocation } from "react-router-dom";
import { GiGreenhouse } from 'react-icons/gi';
import { BimLinkItem } from './types'
import { themedUseStyletron } from '../../../theme/customTheme'
import { hexToRgb } from '../../../utils/colorUtils'

import { IAutomationObject } from '../../../automationObjects'
import { GetObjectUrl } from '../../../utils/navigationService';

// ------------------------------------------------------------------------------
// BIM Viewer Wrapper for React
// from https://github.com/xBimTeam/XbimWebUI/tree/master/examples/basic-viewer
// ------------------------------------------------------------------------------

function BimViewerComponent(
    {
        height = 300,
        width = 300,
        //plugins = [],
        fallbackContent,
        data,
        fileUrl = '',
        error = '',
        bimElement = '',
        ...props
    }: BimViewerProps,
    ref: ForwardedRef<Viewer>
) {

    const [, theme] = themedUseStyletron();
    type TypedBimViewer = Viewer;

    const nav = useNavigate();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const viewerRef = useRef<TypedBimViewer | null>();


    repaintViewer();

    function navigate(url) {
        nav(url);
    }


    const renderViewer = () => {


        if (!canvasRef.current) return;

        const viewer = new Viewer(canvasRef.current);
        viewerRef.current = viewer
        canvasRef.current.width = width;
        canvasRef.current.height = height;

        const cube = new NavigationCube();
        cube.ratio = 0.05;
        cube.passiveAlpha = cube.activeAlpha = 0.85;

        const grid = new Grid();
        grid.colour = [13, 19, 13];
        grid.zFactor = 20;

        const loader = new LoaderOverlay();

        viewer.addPlugin(grid);
        viewer.addPlugin(cube);
        viewer.addPlugin(loader);

        viewer.renderingMode = RenderingMode.NORMAL;

        viewer.background = [254, 254, 254];
        viewer.start();
        loader.show();
        viewer.loadAsync(fileUrl);


        viewer.highlightingColour = [255, 200, 1, 255];

        viewer.on("loaded", (evt) => {
            loader.hide();
            viewer.show(ViewType.DEFAULT, undefined, 1, true);
            error = '';
           // viewer.width = 500;
            repaintViewer();
        });

        viewer.on("error", (arg) => {
            //preppend error report
            console.log(arg);
            error = arg.message;
        });

        viewer.on('pointerdown', (args) => {
            console.log(args);

            if (args.id === null)
                return;
       
            if (data) {
                for (var x in data) {
                    if (data[x].bimId === args.id) {
                        navigate(GetObjectUrl(data[x].resourceGroupId,data[x].id));
                    }
                } 
            }
        });

        viewer.on("dblclick", (arg) => {
            console.log(arg);
            //viewer.addState(State.HIDDEN, [arg.id], arg.model);
        });

        viewer.on('pick', args => {
            console.log(args.xyz);
            var state = viewer.getState(args.id, args.model);
            if (state === State.HIGHLIGHTED) {
                viewer.removeState(State.HIGHLIGHTED, [args.id], args.model);
            } else {
                viewer.addState(State.HIGHLIGHTED, [args.id], args.model);
                //viewer.show(ViewType.DEFAULT, args.id, undefined, false);
            }

        });

        reforwardRef(ref, viewerRef.current);
    };

    function repaintViewer() {

        if (viewerRef.current && data ) { //&& viewerRef.current.isModelLoaded) {
            var iGreen;
            var iRed;
            var iGray;
            var iBlue;
            var iGrayBase;
            for (var i = 0; i < 254; i++) {
                var style = viewerRef.current.getStyle(i);
                if (style === null) {
                    iGreen = i;
                    iRed = i + 1;
                    iGray = i + 2;
                    iBlue = i + 3;
                    iGrayBase = i + 4;
                    var g = hexToRgb(theme.appColors.aggregate.on);
                    if ( g ) 
                    {
                        viewerRef.current.defineStyle(iGreen, [g.r, g.g, g.b, 255]);
                    }
                    else
                    {
                        viewerRef.current.defineStyle(iGreen, [37, 190, 47, 255]);
                    }
                    var r = hexToRgb(theme.appColors.aggregate.error);
                    if ( r ) 
                    {
                        viewerRef.current.defineStyle(iRed, [r.r, r.g, r.b, 255]);
                    }
                    else
                    {
                        viewerRef.current.defineStyle(iRed, [255, 37, 0, 255]);
                    }
                    var gr = hexToRgb(theme.appColors.aggregate.off);
                    if ( gr ) 
                    {
                        viewerRef.current.defineStyle(iGray, [gr.r, gr.g, gr.b, 255]);
                    }
                    else
                    {
                        viewerRef.current.defineStyle(iGray, [217, 217, 212, 255]);
                    }        
                    var b = hexToRgb(theme.appColors.aggregate.man);           
                    if ( b ) 
                    {
                        viewerRef.current.defineStyle(iBlue, [b.r, b.g, b.b, 255]);
                    }
                    else
                    {
                        viewerRef.current.defineStyle(iBlue, [80, 150, 230, 255]);
                    } 

                    viewerRef.current.defineStyle(iGrayBase, [217, 217, 212, 100]);
                    break;
                }
            }

            /* //try to set the Model Opacity ... not working            
                        let arrayBase;
                        for (var y in ProductType) {
                            var type = ProductType[y];
                            if (!/^[0-9]+$/.test(type)) {
                                continue;
                            }
                            var nTyp = parseFloat(type);
                            
                            if (arrayBase == null) {
                                arrayBase = [nTyp];
                            }
                            else {
                                arrayBase.push(nTyp);
                            }
                            
                        }
                        if (  typeof arrayBase !== "undefined" && arrayBase.length !== 0){
                            viewerRef.current.setStyle(iGrayBase, arrayBase);
                        }
            */

            let arrayGreen;
            let arrayRed;
            let arrayBlue;
            let arrayGray;
            var last;
            if (data) {
                for (var x in data) {
                    if (data[x].errSum) {
                        if (arrayRed == null) {
                            arrayRed = [data[x].bimId];
                        }
                        else {
                            arrayRed.push(data[x].bimId);
                        }
                    }
                    else if (data[x].isMan) {
                        if (arrayBlue == null) {
                            arrayBlue = [data[x].bimId];
                        }
                        else {
                            arrayBlue.push(data[x].bimId);
                        }
                    }
                    else if (data[x].isOn) {
                        if (arrayGreen == null) {
                            arrayGreen = [data[x].bimId];
                        }
                        else {
                            arrayGreen.push(data[x].bimId);
                        }
                    }
                    else {
                        if (arrayGray == null) {
                            arrayGray = [data[x].bimId];
                        }
                        else {
                            arrayGray.push(data[x].bimId);
                        }
                    }
                    last = data[x].bimId;
                };
                if (typeof arrayGreen !== "undefined" && arrayGreen.length !== 0) {
                    viewerRef.current.setStyle(iGreen, arrayGreen);
                }
                if (typeof arrayRed !== "undefined" && arrayRed.length !== 0) {
                    viewerRef.current.setStyle(iRed, arrayRed);
                }
                if (typeof arrayBlue !== "undefined" && arrayBlue.length !== 0) {
                    viewerRef.current.setStyle(iBlue, arrayBlue);
                }
                if (typeof arrayGray !== "undefined" && arrayGray.length !== 0) {
                    viewerRef.current.setStyle(iGray, arrayGray);
                }

                if (bimElement)
                    viewerRef.current.show(ViewType.DEFAULT, bimElement, undefined, true);
            }
        }
    }

    const destroyViewer = () => {
        reforwardRef(ref, null);

        if (viewerRef.current) {
            viewerRef.current.stopAll();
            viewerRef.current = null;
        }
    };


    useEffect(() => {
        if (!viewerRef.current) return;

        if (fileUrl) {
            destroyViewer();
            setTimeout(renderViewer);
        } else {

        }
    }, [fileUrl]);

    useEffect(() => {
        if (!viewerRef.current) return;

        if (data) {
            repaintViewer();
        } else {

        }
    }, [data]);
 

    useEffect(() => {
        renderViewer();

        return () => destroyViewer();
    }, []);

    return (
        <div className="flex-container">
            <div id="viewer-container">
                <canvas ref={canvasRef} height={height} width={width} {...props}>
                    {fallbackContent}
                </canvas>
            </div>
        </div >
    );
}


export const XBimViewer = forwardRef(BimViewerComponent) as TypedBimViewerComponent;